import { Button } from '@akin-home/buttons/native';
import { Text } from '@akin-home/text/native';
import styled from 'styled-components/native';
import { shallow } from 'zustand/shallow';
import CircularProgress from '../../components/CircularProgress';
import { useStateStore } from '../../stores/useStateStore';
import { palette } from '../../theme';
import DashboardPanel from '../DashboardPanel';

interface Props {
  small?: boolean;
}

export const TimerDashboardPanel = ({ small }: Props) => {
  const { is_running, paused, minutes, current_minute_value, progress } =
    useStateStore(
      ({ timer }) => ({
        is_running: timer.is_running,
        paused: timer.paused,
        minutes: timer.minutes,
        current_minute_value: timer.current_minute_value,
        progress: timer.progress,
      }),
      shallow
    );

  const start = useStateStore((state) => state.timer.start);
  const reset = useStateStore((state) => state.timer.stop);
  const increase = useStateStore((state) => state.timer.increment);
  const decrease = useStateStore((state) => state.timer.decrement);
  const pause = useStateStore((state) => state.timer.pause);
  const resume = useStateStore((state) => state.timer.resume);

  return (
    <Panel>
      <TitleRow>
        <Text weight="semibold">Timer</Text>
      </TitleRow>

      <Body>
        {is_running ? (
          <ControlsContainer>
            <TimerButtons>
              {!small ? (
                <ButtonContainer>
                  <Button
                    onPress={reset}
                    size="xlarge"
                    variant="secondary"
                    icon="arrow-rotate-left"
                    iconSize={25}
                  />

                  <Text>Reset</Text>
                </ButtonContainer>
              ) : null}

              <CircularProgress
                value={progress}
                radius={80}
                rotation={0}
                activeStrokeColor={palette.Primary_blue}
                inActiveStrokeColor="rgba(230, 232, 235, 1)"
                strokeLinecap="butt"
              >
                <Text variant="h2" weight="semibold">
                  {current_minute_value}
                </Text>
                <Text>Mins</Text>
              </CircularProgress>

              {!small ? (
                <ButtonContainer>
                  {paused ? (
                    <Button
                      onPress={resume}
                      size="xlarge"
                      icon="play"
                      iconSize={25}
                    />
                  ) : (
                    <Button
                      onPress={pause}
                      size="xlarge"
                      icon="pause"
                      iconSize={25}
                    />
                  )}

                  <Text>{paused ? 'Resume' : 'Pause'}</Text>
                </ButtonContainer>
              ) : null}
            </TimerButtons>

            {small ? (
              <TimerButtons>
                <Button icon="arrow-rotate-left" variant="text" onPress={reset}>
                  Reset
                </Button>

                <Button
                  icon={paused ? 'play' : 'pause'}
                  onPress={paused ? resume : pause}
                >
                  {paused ? 'Resume' : 'Pause'}
                </Button>
              </TimerButtons>
            ) : null}
          </ControlsContainer>
        ) : (
          <ControlsContainer>
            <TimerButtons>
              <ButtonContainer>
                <Button
                  onPress={decrease}
                  size="xlarge"
                  variant="secondary"
                  icon="minus"
                />

                {small ? null : <Text>Decrease</Text>}
              </ButtonContainer>

              <SettingTimeContainer>
                <Text variant="h2" weight="semibold">
                  {minutes}
                </Text>
                <Text>Mins</Text>
              </SettingTimeContainer>

              <ButtonContainer>
                <Button
                  onPress={increase}
                  size="xlarge"
                  variant="secondary"
                  icon="plus"
                />

                {small ? null : <Text>Increase</Text>}
              </ButtonContainer>
            </TimerButtons>

            <Button size="large" icon="play" onPress={start}>
              Start
            </Button>
          </ControlsContainer>
        )}
      </Body>
    </Panel>
  );
};

const Panel = styled(DashboardPanel)`
  flex: 1;
  padding: 15px;
  align-items: center;
  gap: 20px;
`;

const Body = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
`;

const ControlsContainer = styled.View`
  gap: 20px;
`;

const TitleRow = styled.View`
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
`;

const TimerButtons = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 32px;
`;

const SettingTimeContainer = styled.View`
  background-color: rgba(5, 22, 51, 0.1);

  border-radius: 20px;

  align-items: center;

  min-width: 120px;

  padding: 10px;
`;

const ButtonContainer = styled.View`
  align-items: center;
  gap: 16px;
`;
